import { useMemo } from 'react';
import type { VoidFunctionComponent } from 'react';

import Badge from '../Badge/Badge';
import Icon, { icons } from '../Icon';
import Tooltip, { TooltipContent } from '../Tooltip';
import type { VulnBadgeColor } from '../Badge/Badge';

type Props = {
  score: number;
  hasTooltip?: boolean;
  showScore?: boolean;
};

type BadgeConfig = {
  label: string;
  color: VulnBadgeColor;
};

const useBadgeConfig = (score: number): BadgeConfig =>
  useMemo(() => {
    if (score < 0 || score > 100) {
      return {
        label: 'Unknown',
        color: 'unknown-severity-vuln',
      };
    }
    if (score >= 90) {
      return {
        label: 'Critical',
        color: 'critical-severity-vuln',
      };
    }
    if (score >= 70) {
      return {
        label: 'High',
        color: 'high-severity-vuln',
      };
    }
    if (score >= 40) {
      return {
        label: 'Medium',
        color: 'medium-severity-vuln',
      };
    }

    return {
      label: 'Low',
      color: 'low-severity-vuln',
    };
  }, [score]);

/**
 * Displays a colour badge based on the custom risk score (0-100).
 *
 * Thresholds:
 *  90-100 = Critical
 *  70-89  = High
 *  40-69  = Medium
 *  0-39   = Low
 */
const CustomRiskScoreBadge: VoidFunctionComponent<Props> = ({
  score,
  hasTooltip = true,
  showScore = true,
}) => {
  const config = useBadgeConfig(score);

  return (
    <Tooltip
      showTooltip={hasTooltip}
      id={`custom-risk-score-badge-${config.label}-${score}`}
      tooltip={
        <TooltipContent>
          <div>{config.label} custom risk score: {score}</div>
        </TooltipContent>
      }
    >
      {/* Wrapper div needed so OverlayTrigger can attach event handlers to a DOM element */}
      <div>
        <Badge color={config.color}>
          <Icon icon={icons.asterisk} /> {config.label}
          {showScore && ` ${score}`}
        </Badge>
      </div>
    </Tooltip>
  );
};

export default CustomRiskScoreBadge;
